import React, { useEffect, useRef } from 'react';

const ACCENT = '#f97316';
const BG = '#020202';
const CAM_DIST = 6;
const FOCAL = 145;
const SPHERE_RADIUS = 6.5;

// The cursor core echoes the swarm it sits in front of: a tiny nucleus with
// its own electrons, each shell tilted so they read as orbits, not a flat ring.
const CORE_ELECTRONS = [
  { rx: 1, ry: 0.4, tilt: 0.5, speed: 2.6, phase: 0 },
  { rx: 1, ry: 0.4, tilt: -1.0, speed: -2.1, phase: 2.3 },
  { rx: 1, ry: 0.4, tilt: 1.9, speed: 1.7, phase: 4.6 },
];

// Scale particle density with viewport area so the swarm stays visible on large/high-res screens.
const particleCountFor = (width, height) =>
  Math.round(Math.min(3200, Math.max(1200, (width * height) / 1100)));

const rotatePoint = ([x, y, z], rx, ry) => {
  const y1 = y * Math.cos(rx) - z * Math.sin(rx);
  const z1 = y * Math.sin(rx) + z * Math.cos(rx);
  const x2 = x * Math.cos(ry) + z1 * Math.sin(ry);
  const z2 = -x * Math.sin(ry) + z1 * Math.cos(ry);
  return [x2, y1, z2];
};

const project = ([x, y, z], cx, cy) => {
  const zc = z + CAM_DIST;
  const scale = FOCAL / zc;
  return { x: cx + x * scale, y: cy - y * scale, zc };
};

const lerp = (a, b, t) => a + (b - a) * t;

const ConcreteBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const mouse = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const state = { hover: false, clicked: false, speed: 1, coreX: mouse.x, coreY: mouse.y };

    let width = 0;
    let height = 0;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const particles = Array.from({ length: particleCountFor(width, height) }, () => {
      const r = SPHERE_RADIUS * Math.cbrt(Math.random());
      const theta = Math.random() * 2 * Math.PI;
      const phi = Math.acos(2 * Math.random() - 1);
      return [
        r * Math.sin(phi) * Math.cos(theta),
        r * Math.sin(phi) * Math.sin(theta),
        r * Math.cos(phi),
      ];
    });
    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };
    const handlePointerOver = (e) => {
      state.hover = !!e.target.closest('a, button, [role="button"], input, textarea, select, .clickable');
    };
    const handleMouseDown = () => { state.clicked = true; };
    const handleMouseUp = () => { state.clicked = false; };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('pointerover', handlePointerOver);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);

    let raf;
    let last = performance.now();
    let rotX = 0.4;
    let rotY = 0.3;
    let coreScale = 2.2;
    let elapsed = 0;

    const renderFrame = (dt) => {
      const cx = width / 2;
      const cy = height / 2;

      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = BG;
      ctx.fillRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'lighter';

      // Swarm: a rotating sphere of points, orbiting the center.
      // Kept deliberately calm at rest — this runs behind body text on every
      // page, and fast/bright motion there fights with reading.
      const targetSpeed = state.hover ? 3 : 0.4;
      state.speed = lerp(state.speed, targetSpeed, 0.05);
      rotX += dt * 0.025 * state.speed;
      rotY += dt * 0.02 * state.speed;

      for (const p of particles) {
        const [rx, ry, rz] = rotatePoint(p, rotX, rotY);
        const { x, y, zc } = project([rx, ry, rz], cx, cy);
        const depth = 1 - (zc - (CAM_DIST - SPHERE_RADIUS)) / (2 * SPHERE_RADIUS);
        const clampedDepth = Math.max(0, Math.min(1, depth));
        const size = 2 + clampedDepth * 3.2;
        ctx.globalAlpha = 0.32 + clampedDepth * 0.38;
        ctx.fillStyle = ACCENT;
        ctx.fillRect(x - size / 2, y - size / 2, size, size);
      }

      // Core: a small glowing nucleus with a few electrons orbiting it — the
      // same atom the swarm is built from, just close enough to see.
      state.coreX = lerp(state.coreX, mouse.x, 0.18);
      state.coreY = lerp(state.coreY, mouse.y, 0.18);
      const targetCoreScale = state.clicked ? 0.75 : state.hover ? 1.6 : 1;
      coreScale = lerp(coreScale, targetCoreScale, 0.15);
      const orbitRadius = coreScale * 11;
      const orbitSpeedMul = state.hover ? 2.2 : 0.8;
      const glowAlpha = state.hover ? 0.4 : 0.22;
      const nucleusAlpha = state.hover ? 1 : 0.85;

      ctx.save();
      ctx.translate(state.coreX, state.coreY);

      const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, orbitRadius * 3);
      glow.addColorStop(0, `rgba(249, 115, 22, ${glowAlpha})`);
      glow.addColorStop(1, 'rgba(249, 115, 22, 0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(0, 0, orbitRadius * 3, 0, Math.PI * 2);
      ctx.fill();

      CORE_ELECTRONS.forEach((e) => {
        const angle = e.phase + elapsed * e.speed * orbitSpeedMul;
        const ex = Math.cos(angle) * orbitRadius * e.rx;
        const ey = Math.sin(angle) * orbitRadius * e.ry;
        const x = ex * Math.cos(e.tilt) - ey * Math.sin(e.tilt);
        const y = ex * Math.sin(e.tilt) + ey * Math.cos(e.tilt);
        ctx.globalAlpha = nucleusAlpha * 0.8;
        ctx.fillStyle = ACCENT;
        ctx.beginPath();
        ctx.arc(x, y, 1.6, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.globalAlpha = nucleusAlpha;
      ctx.fillStyle = ACCENT;
      ctx.beginPath();
      ctx.arc(0, 0, Math.max(2, orbitRadius * 0.24), 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      ctx.globalAlpha = 1;
      elapsed += dt;
    };

    if (reduceMotion) {
      renderFrame(0);
    } else {
      const loop = (now) => {
        const dt = Math.min((now - last) / 1000, 0.05);
        last = now;
        renderFrame(dt);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('pointerover', handlePointerOver);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="concrete-bg-container"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
      }}
    />
  );
};

export default ConcreteBackground;
